/**
 * Scoring. [P1-score-live] [P1-score-farms] [P1-score-end]
 *
 * Three separate jobs live here, and they follow different rules:
 *
 *   1. During play — a feature that COMPLETES scores immediately and its meeples come home.
 *   2. At the end   — everything still unfinished scores, at a reduced rate, and stays put.
 *   3. Farms        — score only at the end, and by a rule unlike anything else in the game.
 *
 * The majority rule is shared by all three: whoever has the most meeples on a feature takes the
 * points, and if several players tie for the most, they EACH take the full amount. Nobody splits.
 */
import { describeFeature, find, nodeId, parseNodeId, roots } from './features.js';
import { MEEPLE_STRENGTH, posKey } from './types.js';
/** Meeples standing on any node of a feature. */
export function occupantsOf(forest, meeples, root) {
    return meeples.filter((m) => find(forest, nodeId(m.pos, m.featureIndex)) === root);
}
/**
 * Who scores a feature. The most meeples wins; ties all take the full amount.
 *
 * A player holding meeples on both halves of a merged city counts them all — which is exactly why
 * merging is worth doing, and why the count is over meeples rather than over players.
 *
 * Counted by STRENGTH, not by head. The base game has only plain followers so the two are identical
 * today, but the big follower exists precisely to break that: one body, two votes. Writing the sum
 * this way now means an expansion adds a row to `MEEPLE_STRENGTH` instead of editing this function.
 */
export function majoritySeats(occupants) {
    if (occupants.length === 0)
        return [];
    const counts = new Map();
    for (const m of occupants) {
        counts.set(m.seat, (counts.get(m.seat) ?? 0) + (MEEPLE_STRENGTH[m.kind] ?? 1));
    }
    const best = Math.max(...counts.values());
    return [...counts.entries()].filter(([, n]) => n === best).map(([seat]) => seat).sort((a, b) => a - b);
}
/** Points a completed feature is worth, during play. */
export function completedValue(info) {
    switch (info.kind) {
        case 'road':
            return info.tiles.length;
        case 'city':
            // Two per tile plus two per pennant — a completed city is the big earner.
            return info.tiles.length * 2 + info.pennants * 2;
        case 'cloister':
            return 9; // the cloister itself plus its eight neighbours
        case 'field':
            return 0; // farms never complete; they score at the end, by a different rule
    }
}
/** Points an unfinished feature is worth at the end of the game. */
export function endValue(info, board) {
    switch (info.kind) {
        case 'road':
            return info.tiles.length;
        case 'city':
            // Half rate, and pennants drop to one apiece.
            return info.tiles.length + info.pennants;
        case 'cloister': {
            // One point for the cloister's own tile plus one for each neighbour that got filled.
            const first = info.nodes[0];
            if (!first)
                return 0;
            const { pos } = parseNodeId(first);
            let count = 1;
            for (let dx = -1; dx <= 1; dx++) {
                for (let dy = -1; dy <= 1; dy++) {
                    if (dx === 0 && dy === 0)
                        continue;
                    if (board.has(posKey({ x: pos.x + dx, y: pos.y + dy })))
                        count++;
                }
            }
            return count;
        }
        case 'field':
            return 0; // handled by scoreFarms
    }
}
/**
 * Every feature that just completed and has at least one meeple on it.
 *
 * Placing a single tile can complete more than one feature at once — a tile that closes a city, closes
 * a road, and finishes a neighbouring cloister all in one move is legal and scores all three.
 */
export function scoreCompletions(forest, board, tiles, meeples, candidateRoots) {
    const out = [];
    const seen = new Set();
    for (const candidate of candidateRoots) {
        const root = find(forest, candidate);
        if (seen.has(root))
            continue;
        seen.add(root);
        const info = describeFeature(forest, board, tiles, root);
        if (!info || !info.complete || info.kind === 'field')
            continue;
        const occupants = occupantsOf(forest, meeples, root);
        // An unoccupied feature still completes — it just scores nothing and returns nobody.
        const winners = majoritySeats(occupants);
        const points = completedValue(info);
        out.push({
            root,
            kind: info.kind,
            tileCount: info.tiles.length,
            pennants: info.pennants,
            points,
            awards: winners.map((seat) => ({ seat, points })),
            returned: occupants,
        });
    }
    return out;
}
/** Everything unfinished, scored at end of game. Farms are excluded — see `scoreFarms`. */
export function scoreEndgameFeatures(forest, board, tiles, meeples) {
    const out = [];
    for (const root of roots(forest)) {
        const info = describeFeature(forest, board, tiles, root);
        if (!info || info.kind === 'field')
            continue;
        const occupants = occupantsOf(forest, meeples, root);
        if (occupants.length === 0)
            continue; // nobody there, nothing to award
        const winners = majoritySeats(occupants);
        const points = endValue(info, board);
        out.push({
            root,
            kind: info.kind,
            tileCount: info.tiles.length,
            pennants: info.pennants,
            points,
            awards: winners.map((seat) => ({ seat, points })),
            returned: [],
        });
    }
    return out;
}
/**
 * Farm scoring — the end-of-game rule that decides most close games, and the one implementations
 * most often get wrong.
 *
 * A farmer scores **3 points for every COMPLETED city that his farm supplies**. "Supplies" means the
 * farm touches that city somewhere along its length: on any tile of the farm, one of that tile's field
 * features lists a city feature as adjacent, and that city — followed through the union-find to its
 * root — turned out to be finished.
 *
 * Two consequences worth stating, because both are counter-intuitive:
 *   - An unfinished city is worth nothing to a farmer. Not half, nothing.
 *   - A single city counts ONCE per farm, however many tiles of that farm touch it. Hence the set.
 */
export function scoreFarms(forest, board, tiles, meeples) {
    const out = [];
    for (const root of roots(forest)) {
        const info = describeFeature(forest, board, tiles, root);
        if (!info || info.kind !== 'field')
            continue;
        const occupants = occupantsOf(forest, meeples, root);
        if (occupants.length === 0)
            continue;
        // Collect the distinct cities this farm touches, by their union-find root.
        const cityRoots = new Set();
        for (const node of info.nodes) {
            const { pos, featureIndex } = parseNodeId(node);
            const placed = board.get(posKey(pos));
            if (!placed)
                continue;
            const def = tiles.get(placed.defId);
            if (!def)
                continue;
            const feature = def.features[featureIndex];
            if (!feature || feature.kind !== 'field')
                continue;
            for (const cityIndex of feature.adjacentCities ?? []) {
                cityRoots.add(find(forest, nodeId(pos, cityIndex)));
            }
        }
        let completedCities = 0;
        for (const cityRoot of cityRoots) {
            const city = describeFeature(forest, board, tiles, cityRoot);
            if (city && city.kind === 'city' && city.complete)
                completedCities++;
        }
        const points = completedCities * 3;
        if (points === 0)
            continue;
        const winners = majoritySeats(occupants);
        out.push({
            root,
            kind: 'field',
            tileCount: info.tiles.length,
            pennants: 0,
            points,
            awards: winners.map((seat) => ({ seat, points })),
            returned: [],
        });
    }
    return out;
}
//# sourceMappingURL=scoring.js.map